/**
 * Preseason aggregate metrics for company portal cards.
 * Finalized monetary fields should come from finalized contract snapshots (immutable values).
 */

const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

/** Arithmetic mean of price_per_kg on a contract's crop_prices rows. */
export function avgCropPricePerKg(cropPrices) {
    const rows = Array.isArray(cropPrices) ? cropPrices : [];
    if (rows.length === 0) return 0;
    const sum = rows.reduce((s, row) => s + num(row?.price_per_kg), 0);
    return sum / rows.length;
}

function cropIdFromApplication(app) {
    const c = app?.crop;
    if (c == null) return null;
    return typeof c === 'object' ? c?.id ?? c?.pk : c;
}

function priceForCrop(cropPrices, cropId) {
    if (cropId == null) return 0;
    const rows = Array.isArray(cropPrices) ? cropPrices : [];
    const row = rows.find((r) => {
        const cid = r?.crop_id ?? (typeof r?.crop === 'object' ? r?.crop?.id : r?.crop);
        return cid === cropId || String(cid) === String(cropId);
    });
    return row ? num(row.price_per_kg) : 0;
}

/** Approved kg for display/value: prefer immutable snapshot, then brief application. */
export function approvedQtyKgForFinalContract(fc) {
    const snap = fc?.snapshot_json || {};
    const snapApp = snap?.application || {};
    return num(snapApp?.approved_qty_kg ?? fc?.application?.approved_qty_kg);
}

/**
 * Agreed list price per kg from snapshot crop_prices for the application crop,
 * else legacy `application.final_price_per_kg` (matches dashboard "amount contracted" logic).
 */
export function agreedPricePerKgForFinalContract(fc) {
    const snap = fc?.snapshot_json || {};
    const snapApp = snap?.application || {};
    const snapContract = snap?.contract || {};
    const cropId = snapApp?.crop_id ?? cropIdFromApplication(fc?.application);
    const snapPrice = priceForCrop(snapContract?.crop_prices, cropId);
    const appFinalPrice = num(fc?.application?.final_price_per_kg);
    return snapPrice > 0 ? snapPrice : appFinalPrice;
}

function cropLabelFromApp(app, cropId) {
    const crop = app?.crop;
    if (crop && typeof crop === 'object') {
        return crop?.name ?? crop?.label ?? (cropId != null ? `Crop #${cropId}` : '—');
    }
    const rows = Array.isArray(app?.contract?.crop_prices) ? app.contract.crop_prices : [];
    if (cropId != null) {
        const matched = rows.find((r) => {
            const cid = typeof r?.crop === 'object' ? r?.crop?.id : r?.crop;
            return cid === cropId || String(cid) === String(cropId);
        });
        if (matched?.crop_name) return matched.crop_name;
    }
    return cropId != null ? `Crop #${cropId}` : '—';
}

/**
 * @param {object[]} contracts - Preseason contract rows from API
 * @param {object[]} applications - Contract applications (with nested contract.crop_prices when present)
 * @param {object[]} finalContracts - Final contracts rows (with snapshot_json when available)
 */
export function computePreseasonStats(contracts, applications, finalContracts = []) {
    const list = Array.isArray(contracts) ? contracts : [];
    const apps = Array.isArray(applications) ? applications : [];
    const finals = Array.isArray(finalContracts) ? finalContracts : [];

    let contractCount = list.length;
    let applicationCount = apps.length;
    let totalQtyAvailable = 0;
    let totalQtyReserved = 0;
    let totalQuantityKg = 0;
    let valueAtListPrices = 0;
    let outstandingValue = 0;

    for (const c of list) {
        const avail = num(c?.available_qty_kg);
        const res = num(c?.reserved_qty_kg);
        const total = num(c?.total_quantity_kg);
        const avg = avgCropPricePerKg(c?.crop_prices);

        totalQtyAvailable += avail;
        totalQtyReserved += res;
        totalQuantityKg += total;
        valueAtListPrices += total * avg;
        outstandingValue += avail * avg;
    }

    /** Finalized value should be immutable: use finalized contract snapshots. */
    let amountContracted = 0;
    let finalizedContractCount = 0;
    let finalizedQtyKg = 0;
    const cropMix = {};
    for (const app of apps) {
        const st = (app?.status || '').toLowerCase();
        if (!['approved', 'finalized'].includes(st)) continue;
        const qty = num(app?.approved_qty_kg);
        const contract = app?.contract;
        const cropId = cropIdFromApplication(app);
        const price = priceForCrop(contract?.crop_prices, cropId);
        const value = qty * price;
        const key = cropId != null ? String(cropId) : cropLabelFromApp(app, cropId);
        if (!cropMix[key]) {
            cropMix[key] = {
                label: cropLabelFromApp(app, cropId),
                qtyKg: 0,
                value: 0,
            };
        }
        cropMix[key].qtyKg += qty;
        cropMix[key].value += value;
    }

    for (const fc of finals) {
        const status = String(fc?.status || '').toLowerCase();
        if (status !== 'finalized') continue;
        finalizedContractCount += 1;

        const qty = approvedQtyKgForFinalContract(fc);
        const price = agreedPricePerKgForFinalContract(fc);
        finalizedQtyKg += qty;
        amountContracted += qty * price;
    }

    const averageContractedPrice = finalizedQtyKg > 0 ? amountContracted / finalizedQtyKg : 0;

    /** Global average price: total list value / total contract kg (same as Value / sum of totals). */
    let averageContractPrice = 0;
    if (totalQuantityKg > 0) {
        averageContractPrice = valueAtListPrices / totalQuantityKg;
    }

    /** Latest price per crop: sort contracts by date_created desc, then take first seen crop_id. */
    const sorted = [...list].sort((a, b) => {
        const ta = a?.date_created ? new Date(a.date_created).getTime() : 0;
        const tb = b?.date_created ? new Date(b.date_created).getTime() : 0;
        return tb - ta;
    });
    const seen = new Set();
    const priceLines = [];
    for (const c of sorted) {
        const rows = Array.isArray(c?.crop_prices) ? c.crop_prices : [];
        for (const row of rows) {
            const cid = typeof row?.crop === 'object' ? row?.crop?.id : row?.crop;
            const key = cid != null ? String(cid) : `name:${row?.crop_name || ''}`;
            if (seen.has(key)) continue;
            seen.add(key);
            const label = row?.crop_name || (cid != null ? `Crop #${cid}` : '—');
            priceLines.push({
                label,
                pricePerKg: num(row?.price_per_kg),
            });
        }
    }

    const mixRows = Object.values(cropMix);
    const totalMixQtyKg = mixRows.reduce((acc, row) => acc + row.qtyKg, 0);
    const totalMixValue = mixRows.reduce((acc, row) => acc + row.value, 0);
    const cropMixContribution = mixRows
        .map((row) => ({
            ...row,
            qtySharePct: totalMixQtyKg > 0 ? (row.qtyKg / totalMixQtyKg) * 100 : 0,
            valueSharePct: totalMixValue > 0 ? (row.value / totalMixValue) * 100 : 0,
        }))
        .sort((a, b) => b.valueSharePct - a.valueSharePct);

    return {
        contractCount,
        applicationCount,
        totalQtyAvailable,
        totalQtyReserved,
        totalQuantityKg,
        valueAtListPrices,
        outstandingValue,
        amountContracted,
        finalizedContractCount,
        finalizedQtyKg,
        averageContractPrice,
        averageContractedPrice,
        priceLines,
        cropMixContribution,
    };
}

function cropIdsMatch(a, b) {
    if (a == null || b == null) return false;
    return String(a) === String(b);
}

function cropIdFromPriceRow(row) {
    if (row == null) return null;
    const c = row.crop;
    if (c != null && typeof c === 'object') return c?.id ?? c?.pk ?? null;
    return row.crop_id ?? row.crop ?? null;
}

function cropLabelFromPriceRow(row, cropId) {
    if (row?.crop_name) return row.crop_name;
    const c = row?.crop;
    if (c && typeof c === 'object') {
        return c?.name ?? c?.label ?? (cropId != null ? `Crop #${cropId}` : '—');
    }
    return cropId != null ? `Crop #${cropId}` : '—';
}

/** Share of contract-level kg fields for this crop when multiple crops are listed (equal split). */
function contractCropShare(contract, cropId) {
    const rows = Array.isArray(contract?.crop_prices) ? contract.crop_prices : [];
    if (rows.length === 0) return 0;
    const has = rows.some((r) => cropIdsMatch(cropIdFromPriceRow(r), cropId));
    if (!has) return 0;
    return 1 / rows.length;
}

/** 1 if no crop filter; else equal-listing share for that crop (0 if crop not on offer). */
export function listingCropShareOnContract(contract, cropId) {
    if (cropId == null || cropId === '' || cropId === 'all') return 1;
    return contractCropShare(contract, cropId);
}

export function filterPreseasonContractsByCrop(contracts, cropId) {
    const list = Array.isArray(contracts) ? contracts : [];
    if (cropId == null || cropId === '' || cropId === 'all') return list;
    return list.filter((c) => contractCropShare(c, cropId) > 0);
}

/**
 * Crops to show in drill-down (union of list prices, applications, finalized snapshots).
 * @returns {{ id: string, label: string }[]}
 */
export function collectPreseasonCropOptions(contracts, applications, finalContracts) {
    const byId = new Map();
    const add = (id, label) => {
        if (id == null) return;
        const sid = String(id);
        const nextLabel = label && String(label).trim() ? String(label).trim() : `Crop #${sid}`;
        if (!byId.has(sid)) {
            byId.set(sid, nextLabel);
            return;
        }
        const cur = byId.get(sid);
        if (cur.startsWith('Crop #') && !nextLabel.startsWith('Crop #')) {
            byId.set(sid, nextLabel);
        }
    };

    for (const c of Array.isArray(contracts) ? contracts : []) {
        for (const row of Array.isArray(c?.crop_prices) ? c.crop_prices : []) {
            const cid = cropIdFromPriceRow(row);
            add(cid, cropLabelFromPriceRow(row, cid));
        }
    }
    for (const app of Array.isArray(applications) ? applications : []) {
        const cid = cropIdFromApplication(app);
        add(cid, cropLabelFromApp(app, cid));
    }
    for (const fc of Array.isArray(finalContracts) ? finalContracts : []) {
        const snap = fc?.snapshot_json || {};
        const snapApp = snap?.application || {};
        const cid = snapApp?.crop_id ?? cropIdFromApplication(fc?.application);
        const label =
            snapApp?.crop_name ||
            (typeof snapApp?.crop === 'object' ? snapApp?.crop?.name : null) ||
            cropLabelFromApp(fc?.application, cid);
        add(cid, label);
    }

    return [...byId.entries()]
        .map(([id, label]) => ({ id, label }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

/**
 * Per-crop preseason metrics for company portal drill-down.
 * Contract total / available / reserved are split equally across listed crops on each offer
 * when several crops share one capacity pool (see footnote in UI).
 *
 * @param {object} params
 * @param {object[]} params.contracts
 * @param {object[]} params.applications
 * @param {object[]} params.finalContracts
 * @param {Record<string, number>} params.finalizedQtyByContract - finalized kg summed per preseason contract id
 * @param {string|number} params.cropId
 */
export function computePreseasonCropDrilldown({
    contracts,
    applications,
    finalContracts,
    finalizedQtyByContract = {},
    cropId,
}) {
    if (cropId == null || cropId === '') return null;
    const list = Array.isArray(contracts) ? contracts : [];
    const apps = Array.isArray(applications) ? applications : [];
    const finals = Array.isArray(finalContracts) ? finalContracts : [];
    const finMap = finalizedQtyByContract && typeof finalizedQtyByContract === 'object' ? finalizedQtyByContract : {};

    const options = collectPreseasonCropOptions(list, apps, finals);
    const opt = options.find((o) => cropIdsMatch(o.id, cropId));
    const label = opt?.label ?? `Crop #${cropId}`;

    let listedTotalQtyKg = 0;
    let listedAvailableQtyKg = 0;
    let listedReservedQtyKg = 0;
    let listedFinalizedOnContractKg = 0;
    let valueAtListPricesCrop = 0;
    let outstandingValueCrop = 0;

    for (const c of list) {
        const share = contractCropShare(c, cropId);
        if (share <= 0) continue;
        const total = num(c?.total_quantity_kg);
        const avail = num(c?.available_qty_kg);
        const res = num(c?.reserved_qty_kg);
        const contractId = c?.id != null ? String(c.id) : '';
        const finalizedOnContract = num(finMap[contractId]);

        const price = priceForCrop(c?.crop_prices, cropId);
        listedTotalQtyKg += total * share;
        listedAvailableQtyKg += avail * share;
        listedReservedQtyKg += res * share;
        listedFinalizedOnContractKg += finalizedOnContract * share;
        valueAtListPricesCrop += total * share * price;
        outstandingValueCrop += avail * share * price;
    }

    let applicationReservedQtyKg = 0;
    let applicationApprovedOrFinalQtyKg = 0;
    let applicationCountForCrop = 0;
    for (const app of apps) {
        if (!cropIdsMatch(cropIdFromApplication(app), cropId)) continue;
        applicationCountForCrop += 1;
        const st = String(app?.status || '').toLowerCase();
        const qty = num(app?.approved_qty_kg);
        if (st === 'approved') applicationReservedQtyKg += qty;
        if (['approved', 'finalized'].includes(st)) applicationApprovedOrFinalQtyKg += qty;
    }

    let finalizedContractCount = 0;
    let finalizedQtyKg = 0;
    let amountContractedCrop = 0;
    for (const fc of finals) {
        const status = String(fc?.status || '').toLowerCase();
        if (status !== 'finalized') continue;
        const snap = fc?.snapshot_json || {};
        const snapApp = snap?.application || {};
        const fcCropId = snapApp?.crop_id ?? cropIdFromApplication(fc?.application);
        if (!cropIdsMatch(fcCropId, cropId)) continue;
        finalizedContractCount += 1;
        const qty = approvedQtyKgForFinalContract(fc);
        const price = agreedPricePerKgForFinalContract(fc);
        finalizedQtyKg += qty;
        amountContractedCrop += qty * price;
    }

    const averageFinalizedPricePerKg = finalizedQtyKg > 0 ? amountContractedCrop / finalizedQtyKg : 0;
    const averageListedPricePerKg = listedTotalQtyKg > 0 ? valueAtListPricesCrop / listedTotalQtyKg : 0;

    let contractsListingCropCount = 0;
    for (const c of list) {
        if (contractCropShare(c, cropId) > 0) contractsListingCropCount += 1;
    }

    let cropMixQtyKg = 0;
    let cropMixValue = 0;
    for (const app of apps) {
        if (!cropIdsMatch(cropIdFromApplication(app), cropId)) continue;
        const st = String(app?.status || '').toLowerCase();
        if (!['approved', 'finalized'].includes(st)) continue;
        const qty = num(app?.approved_qty_kg);
        const price = priceForCrop(app?.contract?.crop_prices, cropId);
        cropMixQtyKg += qty;
        cropMixValue += qty * price;
    }

    const sortedContracts = [...list].sort((a, b) => {
        const ta = a?.date_created ? new Date(a.date_created).getTime() : 0;
        const tb = b?.date_created ? new Date(b.date_created).getTime() : 0;
        return tb - ta;
    });
    let latestListPricePerKg = 0;
    for (const c of sortedContracts) {
        if (contractCropShare(c, cropId) <= 0) continue;
        const p = priceForCrop(c?.crop_prices, cropId);
        if (p > 0) {
            latestListPricePerKg = p;
            break;
        }
    }

    return {
        cropId: String(cropId),
        label,
        finalizedContractCount,
        finalizedQtyKg,
        amountContractedCrop,
        averageFinalizedPricePerKg,
        applicationReservedQtyKg,
        applicationApprovedOrFinalQtyKg,
        applicationCountForCrop,
        listedTotalQtyKg,
        listedAvailableQtyKg,
        listedReservedQtyKg,
        listedFinalizedOnContractKg,
        valueAtListPricesCrop,
        outstandingValueCrop,
        averageListedPricePerKg,
        contractsListingCropCount,
        cropMixQtyKg,
        cropMixValue,
        latestListPricePerKg,
    };
}

/**
 * Province buckets for preseason map/table. When cropId is set, kg are scaled by {@link listingCropShareOnContract}.
 *
 * @param {object[]} contracts
 * @param {Record<string, number>} finalizedQtyByContract
 * @param {{ provinceNameById?: object, canonicalProvinceName?: (name: string) => string | null, cropId?: string | null }} opts
 */
export function computeQuantityByProvinceFromContracts(contracts, finalizedQtyByContract, opts = {}) {
    const { provinceNameById = {}, canonicalProvinceName, cropId = null } = opts;
    const list = Array.isArray(contracts) ? contracts : [];
    const finMap = finalizedQtyByContract && typeof finalizedQtyByContract === 'object' ? finalizedQtyByContract : {};

    return list.reduce((acc, contract) => {
        const share = listingCropShareOnContract(contract, cropId);
        if (share <= 0) return acc;

        const rawTotal = num(contract?.total_quantity_kg);
        if (rawTotal <= 0) return acc;

        const explicitName = contract?.province_name ?? contract?.province_label;
        let provinceLabel = explicitName;
        if (!provinceLabel) {
            const province = contract?.province;
            if (province && typeof province === 'object') {
                provinceLabel = province?.name ?? province?.label ?? province?.province_name;
            } else if (province != null) {
                const provinceKey = String(province);
                if (provinceNameById[provinceKey]) {
                    provinceLabel = provinceNameById[provinceKey];
                } else if (!(typeof province === 'number' || /^\d+$/.test(provinceKey))) {
                    provinceLabel = provinceKey;
                }
            }
        }
        const canon = typeof canonicalProvinceName === 'function' ? canonicalProvinceName(provinceLabel) : null;
        const key = canon || provinceLabel || 'Unknown';

        const reservedKg = num(contract?.reserved_qty_kg) * share;
        const finalizedRaw = num(
            contract?.finalized_qty_kg
            ?? contract?.total_finalized_qty_kg
            ?? contract?.finalized_quantity_kg
            ?? contract?.finalized_kg
            ?? finMap[String(contract?.id)]
            ?? 0
        );
        const finalizedKg = finalizedRaw * share;

        if (!acc[key]) {
            acc[key] = { qtyKg: 0, reservedKg: 0, finalizedKg: 0, contracts: 0 };
        }
        acc[key].qtyKg += rawTotal * share;
        acc[key].reservedKg += reservedKg;
        acc[key].finalizedKg += finalizedKg;
        acc[key].contracts += 1;
        return acc;
    }, {});
}
